import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../datepicker.css'; // Import our custom styles
import { TaskItem } from '../../../../types/topic';
import { AlertCircle, ArrowRight, X } from 'lucide-react';
import { useDeleteTask, useUpdateTask } from '../../../../hooks/queries/useTasks';
import { logger } from '../../../../utils/logger';
import './taskTimeline.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useCalendarPreferences } from '../../../../hooks/useCalendarPreferences';
import { extractBaseColor, getStatusColor, tailwindToHexColor } from '../../../../utils/colorUtils';
import { useTaskColumns } from '../../../../hooks/queries/useTaskColumns';
import { calculateOptimalPopupPosition } from '../../../../lib/utils';

// Create a localizer factory function based on locale
const getLocalizer = (locale: string) => {
  // Different locales have different first days of week
  const dow = locale === 'en-US' ? 0 : 1; // 0 = Sunday, 1 = Monday

  // Configure moment with the appropriate locale settings
  const momentLocale = locale === 'en-US' ? 'en' : 'en-gb';

  moment.locale(momentLocale, {
    week: {
      dow,
    },
  });

  return momentLocalizer(moment);
};

interface TaskTimelineViewProps {
  topicId: string;
  tasks: TaskItem[];
  isMobile: boolean;
  filterHeader: React.ReactNode; // Filter toolbar component to be included in the layout
}

export function TaskTimelineView({ topicId, tasks, filterHeader }: TaskTimelineViewProps) {
  const [selectedTask, setSelectedTask] = useState<TaskItem | null>(null);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [showTasksForDate, setShowTasksForDate] = useState<Date | null>(null);
  const [panelVisible, setPanelVisible] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);

  // Separate state for move column dropdown
  const [moveDropdownTaskId, setMoveDropdownTaskId] = useState<string | null>(null);
  const [moveDropdownPosition, setMoveDropdownPosition] = useState({ top: 0, left: 0 });

  // Use local state for tasks to allow optimistic updates
  const [localTasks, setLocalTasks] = useState<TaskItem[]>(tasks);

  // Get calendar preferences
  const { locale, datePickerLocale } = useCalendarPreferences();

  // Create a localizer based on user preference
  const localizer = getLocalizer(locale);

  // Get column information for color mapping
  const { data: columns = [] } = useTaskColumns(topicId);
  
  // React Query mutations
  const deleteTaskMutation = useDeleteTask();
  const updateTaskMutation = useUpdateTask();

  // Update local tasks when props change
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  // Close date picker modal and move dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Handle date picker modal
      if (selectedTask) {
        const modal = document.getElementById('date-picker-modal');

        // Don't close if the click is inside the modal
        if (modal && modal.contains(e.target as Node)) {
          return;
        }

        // Don't close if clicking on date picker elements
        const target = e.target as HTMLElement;
        const isDatePicker =
          target.closest('.react-datepicker') ||
          target.closest('.react-datepicker-popper') ||
          target.closest('.react-datepicker-wrapper') ||
          target.closest('.react-datepicker__portal');

        if (isDatePicker) {
          return;
        }

        // Otherwise, close the modal and clear selections
        setSelectedTask(null);
      }

      // Handle move dropdown
      if (moveDropdownTaskId) {
        const dropdown = document.getElementById('move-dropdown');

        // Skip if click is inside the dropdown
        if (dropdown && dropdown.contains(e.target as Node)) {
          return;
        }

        // Skip if click is on the move button (has data-move-button attribute)
        if ((e.target as HTMLElement).closest('[data-move-button]')) {
          return;
        }

        // Otherwise close the dropdown
        setMoveDropdownTaskId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedTask, moveDropdownTaskId]);

  // Effect to handle panel animation
  useEffect(() => {
    // When date is selected, animate panel in
    if (showTasksForDate) {
      setPanelVisible(true);
    } else {
      setPanelVisible(false);
    }
  }, [showTasksForDate]);

  // Filter tasks that have deadlines
  const tasksWithDeadlines = localTasks.filter(task => task.deadline);

  // Function to optimistically update a task locally
  const updateTaskLocally = (taskId: string, updates: Partial<TaskItem>) => {
    logger.component('Updating task locally', { taskId, updates });
    setLocalTasks(currentTasks =>
      currentTasks.map(t => (t.id === taskId ? { ...t, ...updates } : t))
    );
  };

  // Don't show an empty state - always render the calendar
  // (We'll let the empty calendar itself communicate that there are no scheduled tasks)

  // Convert tasks to calendar events
  const calendarEvents = tasksWithDeadlines.map(task => {
    // Parse the deadline date
    const deadlineDate = moment(task.deadline).toDate();

    return {
      id: task.id,
      title: task.title,
      start: deadlineDate,
      end: deadlineDate,
      allDay: true,
      resource: task, // Store the full task as a resource for access in custom components
    };
  });

  // Format columns for color utility - using the extractBaseColor utility
  const columnsForColorUtil = columns.map(col => {
    // Use our utility function to extract the base color
    const baseColor = extractBaseColor(col.color);

    // Use the extracted color or fall back to gray
    const colorName = baseColor || 'gray';

    return {
      status: col.status,
      color: colorName || 'gray',
    };
  });

  // Event styling with overdue tasks in red, others based on status and using our color utilities
  const eventPropGetter = (event: { resource: TaskItem }) => {
    const task = event.resource;

    // Check if task is overdue (past deadline and not done)
    const isOverdue =
      task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';

    let backgroundColor, borderColor, textColor;

    // If task is overdue, use red regardless of status (except 'done')
    if (isOverdue) {
      backgroundColor = tailwindToHexColor('bg-red-100');
      borderColor = tailwindToHexColor('border-red-500/50');
      textColor = tailwindToHexColor('text-red-800');
    } else {
      // Use our color utility to get the right color classes
      const bgClass = getStatusColor(task.status, columnsForColorUtil, 'bg');
      const borderClass = getStatusColor(task.status, columnsForColorUtil, 'border');
      const textClass = getStatusColor(task.status, columnsForColorUtil, 'text');

      // Convert Tailwind classes to hex colors using our utility function
      backgroundColor = tailwindToHexColor(bgClass);
      borderColor = tailwindToHexColor(borderClass);
      textColor = tailwindToHexColor(textClass);
    }

    return {
      style: {
        backgroundColor,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: '4px',
        color: textColor,
        fontWeight: 500,
        cursor: 'pointer', // Make the events look clickable
      },
    };
  };

  // Custom event component to show task details
  const EventComponent = ({ event }: { event: { resource: TaskItem } }) => {
    const task = event.resource;

    // Check if task is overdue (past deadline and not done)
    const isOverdue =
      task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';

    return (
      <div className="task-event-container">
        <div className="task-event-title font-base text-xs whitespace-nowrap overflow-hidden text-ellipsis flex items-center">
          {isOverdue && <AlertCircle size={12} className="text-red-600 mr-1 flex-shrink-0" />}
          <span className="truncate">{task.title}</span>
        </div>
      </div>
    );
  };

  // Function to get tasks for a specific date
  const getTasksForDate = (date: Date): TaskItem[] => {
    // Normalize the date to compare just year, month, day
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    return tasksWithDeadlines.filter(task => {
      if (!task.deadline) return false;
      const taskDate = new Date(task.deadline);
      return (
        taskDate.getFullYear() === compareDate.getFullYear() &&
        taskDate.getMonth() === compareDate.getMonth() &&
        taskDate.getDate() === compareDate.getDate()
      );
    });
  };

  // No drag and drop functionality - using click to edit instead

  // No explicit update handler needed - saving happens directly in date picker onChange

  // This useEffect has been moved to the top of the component to avoid conditional hooks issues

  // Side panel component for displaying tasks for a selected day
  const TaskSidePanel = () => {
    // If no date selected, don't render the panel content
    if (!showTasksForDate) return null;

    const tasksForDay = getTasksForDate(showTasksForDate);

    return (
      <div
        style={{
          padding: '20px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '12px',
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: 600,
              color: '#0f172a',
            }}
          >
            {moment(showTasksForDate).format('MMMM D, YYYY')}
          </h3>
          <button
            onClick={() => {
              // Close the panel with animation
              setPanelVisible(false);
              // After animation, clear the selected date
              setTimeout(() => {
                setShowTasksForDate(null);
                setExpandedTaskId(null);
              }, 300);
            }}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '4px',
              color: '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {tasksForDay.length === 0 ? (
          <div
            style={{
              padding: '40px 0',
              textAlign: 'center',
              color: '#64748b',
            }}
          >
            No tasks scheduled for this day
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              overflowY: 'auto',
              flex: 1,
            }}
          >
            {tasksForDay.map(task => {
              // Simple styling based on task status
              const isOverdue =
                task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done';
              const isExpanded = expandedTaskId === task.id;

              // Use the color utility to get the status color
              let borderColorClass;
              if (isOverdue) {
                borderColorClass = 'border-red-500';
              } else {
                borderColorClass = getStatusColor(task.status, columnsForColorUtil, 'border');
              }

              // Convert to hex for inline style
              const borderColor = tailwindToHexColor(borderColorClass);

              return (
                <div
                  key={task.id}
                  className={`task-panel-item rounded-md bg-slate-50 overflow-hidden transition-shadow duration-200 ${
                    isExpanded ? 'shadow-md' : 'shadow-sm'
                  }`}
                  style={{
                    borderLeft: `4px solid ${borderColor}`,
                  }}
                >
                  {/* Task Card Header - Split into two parts: clickable header and non-clickable content */}
                  <div className="p-3">
                    {/* This part is specifically for toggling */}
                    <div
                      className="cursor-pointer"
                      onClick={e => {
                        // Only toggle if clicking directly on the header area
                        if (e.currentTarget === e.target) {
                          logger.component('Header clicked', { taskId: task.id });
                          setExpandedTaskId(expandedTaskId === task.id ? null : task.id);
                        }
                      }}
                    >
                      <div
                        className="flex justify-between items-center mb-1"
                        onClick={() => {
                          // Toggle expanded state for this task
                          setExpandedTaskId(expandedTaskId === task.id ? null : task.id);
                        }}
                      >
                        <div
                          className="font-base flex items-center cursor-pointer"
                          onClick={e => {
                            e.stopPropagation();
                            // Toggle expanded state for this task
                            setExpandedTaskId(expandedTaskId === task.id ? null : task.id);
                          }}
                        >
                          {isOverdue && (
                            <AlertCircle size={14} className="text-red-600 mr-1.5 flex-shrink-0" />
                          )}
                          {task.title}
                        </div>
                        <div
                          className="text-xs px-2 py-0.5 flex items-center gap-1 text-slate-400 cursor-pointer"
                          onClick={e => {
                            e.stopPropagation();
                            // Toggle expanded state for this task
                            setExpandedTaskId(expandedTaskId === task.id ? null : task.id);
                          }}
                        >
                          {isExpanded ? (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="18 15 12 9 6 15"></polyline>
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="6 9 12 15 18 9"></polyline>
                            </svg>
                          )}
                        </div>
                      </div>

                      {task.description && (
                        <div
                          className="text-sm mt-1.5 opacity-80 leading-snug cursor-pointer"
                          onClick={e => {
                            e.stopPropagation();
                            // Toggle expanded state for this task
                            setExpandedTaskId(expandedTaskId === task.id ? null : task.id);
                          }}
                        >
                          {task.description.length > 100
                            ? task.description.substring(0, 100) + '...'
                            : task.description}
                        </div>
                      )}
                    </div>{' '}
                    {/* Close the clickable heading div */}
                  </div>

                  {/* Action buttons - visible when expanded */}
                  {isExpanded && (
                    <div
                      className="p-3"
                      onClick={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      {/* Action Buttons */}
                      <div
                        className="flex mt-8 gap-2"
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        {/* Edit Date Button */}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            // Mark as handled
                            (e as any).isHandled = true;

                            // Close move dropdown if open
                            if (moveDropdownTaskId) {
                              setMoveDropdownTaskId(null);
                            }

                            // Select task for date picker modal
                            setSelectedTask(task);
                            // Position the date picker within the panel
                            const taskEl = e.currentTarget.closest(
                              '.task-panel-item'
                            ) as HTMLElement;
                            const rect = taskEl?.getBoundingClientRect();
                            if (rect) {
                              setModalPosition({
                                top: rect.bottom + 10,
                                left: rect.left + rect.width / 2 - 140,
                              });
                            } else {
                              // Fallback to center positioning
                              const viewportWidth = window.innerWidth;
                              const viewportHeight = window.innerHeight;
                              setModalPosition({
                                top: viewportHeight / 2 - 200,
                                left: viewportWidth / 2 - 140,
                              });
                            }
                          }}
                          className="flex-1 flex items-center justify-center gap-1.5 text-blue-500 text-xs py-1 px-3 transition-colors hover:text-blue-700 border border-transparent hover:border-blue-200 rounded"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="16" y1="2" x2="16" y2="6"></line>
                            <line x1="8" y1="2" x2="8" y2="6"></line>
                            <line x1="3" y1="10" x2="21" y2="10"></line>
                          </svg>
                          Edit Deadline
                        </button>

                        {/* Move Column Button */}
                        <div className="flex-1">
                          <button
                            type="button"
                            data-move-button={task.id}
                            className="w-full flex items-center justify-center gap-1.5 text-blue-500 text-xs py-1 px-3 transition-colors hover:text-blue-700 border border-transparent hover:border-blue-200 rounded"
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();

                              // Log the action
                              logger.component('Move button clicked', { taskId: task.id });

                              // Close date picker if open
                              if (selectedTask) {
                                setSelectedTask(null);
                              }

                              // Toggle dropdown state
                              if (moveDropdownTaskId === task.id) {
                                setMoveDropdownTaskId(null);
                              } else {
                                // Calculate optimal position for the dropdown
                                const button = e.currentTarget;
                                const rect = button.getBoundingClientRect();

                                const position = calculateOptimalPopupPosition(
                                  { top: rect.bottom, left: rect.left },
                                  { width: 200, height: 250 },
                                  { x: 0, y: 8 }
                                );

                                setMoveDropdownPosition({
                                  top: position.top,
                                  left: position.left,
                                });
                                setMoveDropdownTaskId(task.id);
                              }
                            }}
                            onMouseDown={e => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <ArrowRight size={16} />
                            Move
                          </button>
                        </div>

                        {/* Delete Button - only for Done tasks */}
                        {task.status === 'done' && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              // Mark as handled
                              (e as any).isHandled = true;
                              // Delete task with optimistic UI update
                              if (window.confirm('Are you sure you want to delete this task?')) {
                                // Close the task card immediately
                                setExpandedTaskId(null);
                                // Remove from local state for immediate UI update
                                setLocalTasks(currentTasks =>
                                  currentTasks.filter(t => t.id !== task.id)
                                );
                                // Delete in the database
                                deleteTaskMutation.mutateAsync({ topicId, taskId: task.id });
                              }
                            }}
                            className="flex-1 flex items-center justify-center gap-1.5 text-red-600 text-xs py-1 px-1 transition-colors hover:text-red-700"
                          >
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // This useEffect has been moved to the top of the component to avoid conditional hooks issues

  // Get the currently selected task for the move dropdown
  const selectedMoveTask = moveDropdownTaskId
    ? localTasks.find(task => task.id === moveDropdownTaskId)
    : null;

  return (
    <div className="h-full flex flex-col">
      {/* Main content with header and calendar */}
      <div
        className="w-full flex-1 flex flex-col"
        style={{
          width: panelVisible ? 'calc(100% - 320px)' : '100%',
          transition: 'width 0.3s ease',
          position: 'relative',
          paddingLeft: '16px' /* Add left padding */,
        }}
      >
        {/* Filter Header - will also shrink when panel is visible */}
        {filterHeader}

        {/* Calendar Container */}
        <div className="flex-1 overflow-hidden w-full">
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            views={{ month: true }}
            defaultView={Views.MONTH}
            defaultDate={new Date()}
            // culture is now controlled dynamically by the localizer based on user preference
            style={{ height: '100%', width: '100%' }}
            eventPropGetter={eventPropGetter}
            popup={false}
            selectable={true}
            onNavigate={() => {
              // Clear any selections when navigating
              setSelectedTask(null);
              setPanelVisible(false);
              setTimeout(() => setShowTasksForDate(null), 300);
            }}
            // Handle clicking on a day cell
            onSelectSlot={slotInfo => {
              // Get the date from the selection
              const clickedDate = slotInfo.start;
              // Set the selected date
              setShowTasksForDate(clickedDate);
            }}
            // Make clicking on events also open the panel instead of the task modal
            onSelectEvent={event => {
              // Get the date from the event
              const task = event.resource as TaskItem;
              const taskDate = task.deadline ? new Date(task.deadline) : new Date();
              // Set the selected date to show panel
              setShowTasksForDate(taskDate);
            }}
            formats={{
              dayFormat: date => moment(date).format('D'),
            }}
            components={{
              event: props => {
                return (
                  <div className="task-calendar-event">
                    <EventComponent event={props.event} />
                  </div>
                );
              },
            }}
            tooltipAccessor={null} // Disable default tooltips
          />
        </div>
      </div>

      {/* Side Panel - slides in from right */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '320px',
          transform: panelVisible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease',
          overflow: 'hidden',
          zIndex: 1000,
          height: '100%',
          visibility: panelVisible
            ? 'visible'
            : 'hidden' /* Hide when not visible to prevent affecting layout */,
        }}
      >
        <TaskSidePanel />
      </div>

      {/* Date Picker Modal */}
      {selectedTask && (
        <div
          id="date-picker-modal"
          className="fixed bg-white rounded-lg shadow-xl overflow-hidden floating-date-picker"
          style={{
            top: modalPosition.top,
            left: modalPosition.left,
            width: '280px',
            zIndex: 1100, // Higher z-index to appear above the side panel
          }}
        >
          <div className="flex justify-between items-center px-4 py-3">
            <h3 className="font-medium text-gray-800">Change Deadline</h3>
            <button
              onClick={() => {
                setSelectedTask(null);
              }}
              className="p-1 text-gray-500 hover:text-gray-700 rounded-full"
            >
              <X size={16} />
            </button>
          </div>

          <div className="w-full">
            <DatePicker
              selected={selectedTask.deadline ? new Date(selectedTask.deadline) : new Date()}
              onChange={(date: Date) => {
                if (!selectedTask) return;

                // Save immediately when clicking on a date
                const taskId = selectedTask.id;
                const newDeadline = date.toISOString();

                // Close the modal immediately for better UX
                setSelectedTask(null);

                // Update locally first (optimistic update)
                updateTaskLocally(taskId, { deadline: newDeadline });

                // Then update in the database
                updateTaskMutation.mutateAsync({
                  topicId,
                  taskId,
                  updates: { deadline: newDeadline }
                }).catch(error => {
                  logger.error('Failed to update task deadline', error);
                });
              }}
              inline
              className="border-none w-full"
              calendarClassName="w-full !border-none"
              locale={datePickerLocale}
              dayClassName={date =>
                date.getDate() === new Date().getDate() &&
                date.getMonth() === new Date().getMonth() &&
                date.getFullYear() === new Date().getFullYear()
                  ? 'bg-green-500 text-white rounded-full'
                  : undefined
              }
            />
          </div>
        </div>
      )}

      {/* Move column dropdown - rendered as a portal at the document level */}
      {moveDropdownTaskId &&
        selectedMoveTask &&
        createPortal(
          <div
            id="move-dropdown"
            className="fixed z-[1100] bg-white rounded-md shadow-lg border border-gray-200 overflow-auto"
            style={{
              top: moveDropdownPosition.top,
              left: moveDropdownPosition.left,
              width: '200px',
              maxHeight: '300px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            }}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <div className="px-3 py-2 border-b border-gray-200">
              <h3 className="font-medium text-gray-700">Move to Column</h3>
            </div>

            <div className="py-1 max-h-48 overflow-y-auto" role="menu">
              {columns.map(column => {
                // Apply the color to the button based on column color
                let bgClass = 'bg-white text-slate-700 hover:bg-slate-50';

                // If this is the current status, style it as selected
                if (column.status === selectedMoveTask.status) {
                  // Use the color utility to get proper classes
                  const bgColorClass = getStatusColor(column.status, columnsForColorUtil, 'bg');
                  const textColorClass = getStatusColor(column.status, columnsForColorUtil, 'text');
                  bgClass = `${bgColorClass} ${textColorClass} font-medium`;
                }

                return (
                  <button
                    key={column.id}
                    onClick={e => {
                      e.stopPropagation();
                      e.preventDefault();

                      // Log the action for debugging
                      logger.component('Column selected from popup', {
                        taskId: selectedMoveTask.id,
                        newStatus: column.status,
                      });

                      // Update locally first (optimistic update)
                      updateTaskLocally(selectedMoveTask.id, { status: column.status });

                      // Then update in the database
                      updateTaskMutation.mutateAsync({
                        topicId,
                        taskId: selectedMoveTask.id,
                        updates: { status: column.status }
                      });

                      // Close the dropdown
                      setMoveDropdownTaskId(null);
                    }}
                    disabled={column.status === selectedMoveTask.status}
                    className={`w-full text-left block px-4 py-2 text-xs cursor-pointer ${bgClass} border-l-2 ${
                      column.status === selectedMoveTask.status
                        ? getStatusColor(column.status, columnsForColorUtil, 'border').replace(
                            '/50',
                            ''
                          )
                        : 'border-transparent'
                    }`}
                    role="menuitem"
                  >
                    {column.name}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
